import { useQuery } from '@tanstack/react-query';
import { stylistPerformanceApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business KPIサマリ（件数・GOOD率・FIT率・購入率・平均選定時間）と前月データを取得する。
 * @graph-connects /stylist-performance/summary [calls] BQ KPI集計エンドポイント
 */
export function useKpiSummary(month?: string, stylistId?: string) {
  return useQuery({
    queryKey: queryKeys.stylistPerformance.summary(month, stylistId),
    queryFn: () => stylistPerformanceApi.getSummary(month, stylistId),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business GOOD/BAD評価の月別推移を取得する。個人値と全体平均の2系列を返す。
 * @graph-connects /stylist-performance/evaluation-trend [calls] BQ GOOD/BAD集計エンドポイント
 */
export function useEvaluationTrend(months?: number, stylistId?: string) {
  return useQuery({
    queryKey: queryKeys.stylistPerformance.evaluationTrend(months, stylistId),
    queryFn: () => stylistPerformanceApi.getEvaluationTrend(months, stylistId),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business レンタル履歴一覧をフィルタ・ソート・ページネーション付きで取得する。
 * @graph-connects /stylist-performance/orders [calls] 履歴一覧API
 */
export function useRentalHistory(params: {
  stylistId?: string;
  evaluation?: string;
  selector?: string;
  hasPurchase?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: string;
  sortOrder?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: queryKeys.stylistPerformance.orders(params),
    queryFn: () => stylistPerformanceApi.getOrders(params),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business オーダー詳細（アイテム情報・レビュー・スタイリストコメント・再選定履歴）を取得する。
 * @graph-connects /stylist-performance/orders/:id [calls] オーダー詳細API
 */
export function useOrderDetail(orderId: number | null) {
  return useQuery({
    queryKey: queryKeys.stylistPerformance.orderDetail(orderId),
    queryFn: () => stylistPerformanceApi.getOrderDetail(Number(orderId)),
    enabled: orderId !== null,
    staleTime: 5 * 60_000,
    retry: false,
  });
}
